<script setup lang="ts">
import { useSearchResultsRandomizer } from '~/composables/useSearchResultsRandomizer'
import { useDebounce } from '@vueuse/core'
import { LucideSearch, LucideNewspaper, LucideCircleX, SlidersVertical } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import { EntityCreditItem, EntityCreditItemSkeleton } from '~/components/entity/credit'
import type { ICreditCard, ICreditsQueryParams } from '~/components/entity/credit'
import type { IResponseData, IMultiselectOption } from '~/types'
import type { SearchResponse } from 'meilisearch'
import { Input } from '~/components/ui/input'
import { Button } from '~/components/ui/button'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from '~/components/ui/select'
import { Alert, AlertTitle, AlertDescription } from '~/components/ui/alert'
import { useCreditFilters } from '~/components/widget/credits/list-meili-search/mocks'
import type { ICreditPurposes } from '~/components/widget/select-credit-purpose'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { AmountCurrencySelector } from '~/components/feature/amount-currency-selector'
import type { IOrganization } from '~/components/entity/organization/types'

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()

const { locale } = useI18n()

// Track search query state for credits
const searchQueryState = useState<string>('searchQuery_credits', () => '')

const filtersStore = useCreditsFiltersStore()
const { shuffleHighlightedAfterMount } = useSearchResultsRandomizer()

const { periodOptions, organizationType, incomeConfirmationTypes, guaranteeRequiredTypes } =
  useCreditFilters()

const props = withDefaults(
  defineProps<{
    organization?: IOrganization
    queryParams?: ICreditsQueryParams
  }>(),
  {
    queryParams: () => ({
      per_page: '10',
      q: ''
    }),
    organization: undefined
  }
)
const useLocalFilters = computed(() => !!props.organization)

const hitsPerPage = Number(route.query.per_page || props.queryParams.per_page || 10)

const localSearchQueryString = ref(String(route.query.q || ''))
const localCreditAmountRaw = ref<number | undefined>(undefined)
const localSelectedPeriod = ref<IMultiselectOption[]>([])
const localSelectedOrganizationType = ref<string>(String(organizationType[0].value))
const localSelectedIncomeConfirmation = ref<string | undefined>(undefined)
const localSelectedGuaranteeRequiredType = ref<string | undefined>(undefined)
const localSelectedPurpose = ref<IMultiselectOption[]>([])
const localSelectedType = ref<IMultiselectOption[]>([])
const localCurrency = ref<string>('any')
const localCurrentPage = ref(Number(route.query.page) || 1)

const searchQueryString = computed({
  get: () => (useLocalFilters.value ? localSearchQueryString.value : filtersStore.searchQuery),
  set: (v: string) => {
    if (useLocalFilters.value) localSearchQueryString.value = v
    else filtersStore.searchQuery = v
  }
})

// Watch for search query state changes
watch(
  () => searchQueryState.value,
  (newValue) => {
    // Apply value only if it's not empty, has changed, and differs from current search value
    if (newValue && newValue !== searchQueryString.value) {
      searchQueryString.value = newValue
      // Clear state after applying
      searchQueryState.value = ''
    }
  },
  { immediate: true }
)

const creditAmountRaw = computed<number | undefined>({
  get: () => (useLocalFilters.value ? localCreditAmountRaw.value : filtersStore.creditAmount),
  set: (v) => {
    if (useLocalFilters.value) localCreditAmountRaw.value = v
    else filtersStore.creditAmount = v
  }
})

const currency = computed<string>({
  get: () => (useLocalFilters.value ? localCurrency.value : filtersStore.currency),
  set: (v) => {
    if (useLocalFilters.value) localCurrency.value = v
    else filtersStore.currency = v
  }
})

const selectedPeriod = computed<IMultiselectOption[]>({
  get: () => (useLocalFilters.value ? localSelectedPeriod.value : filtersStore.selectedPeriod),
  set: (v) => {
    if (useLocalFilters.value) localSelectedPeriod.value = v
    else filtersStore.selectedPeriod = v
  }
})

const selectedOrganizationType = computed<string>({
  get: () =>
    useLocalFilters.value
      ? localSelectedOrganizationType.value
      : filtersStore.selectedOrganizationType,
  set: (v) => {
    if (useLocalFilters.value) localSelectedOrganizationType.value = v
    else filtersStore.selectedOrganizationType = v
  }
})

const selectedIncomeConfirmation = computed<string | undefined>({
  get: () =>
    useLocalFilters.value
      ? localSelectedIncomeConfirmation.value
      : filtersStore.selectedIncomeConfirmation,
  set: (v) => {
    if (useLocalFilters.value) localSelectedIncomeConfirmation.value = v
    else filtersStore.selectedIncomeConfirmation = v
  }
})

const selectedGuaranteeRequiredType = computed<string | undefined>({
  get: () =>
    useLocalFilters.value
      ? localSelectedGuaranteeRequiredType.value
      : filtersStore.selectedGuaranteeRequiredType,
  set: (v) => {
    if (useLocalFilters.value) localSelectedGuaranteeRequiredType.value = v
    else filtersStore.selectedGuaranteeRequiredType = v
  }
})

const selectedPurpose = computed<IMultiselectOption[]>({
  get: () => (useLocalFilters.value ? localSelectedPurpose.value : filtersStore.selectedPurpose),
  set: (v) => {
    if (useLocalFilters.value) localSelectedPurpose.value = v
    else filtersStore.selectedPurpose = v
  }
})

const selectedType = computed<IMultiselectOption[]>({
  get: () => (useLocalFilters.value ? localSelectedType.value : filtersStore.selectedType),
  set: (v) => {
    if (useLocalFilters.value) localSelectedType.value = v
    else filtersStore.selectedType = v
  }
})

const currentPage = computed<number>({
  get: () => (useLocalFilters.value ? localCurrentPage.value : filtersStore.page),
  set: (v) => {
    if (useLocalFilters.value) localCurrentPage.value = v
    else filtersStore.page = v
  }
})

const debouncedQuery = useDebounce(searchQueryString, 500)
const creditAmount = useDebounce(creditAmountRaw, 500)

// Static search key - we'll use manual refresh instead of reactive key
const searchKey = computed(() => {
  // Include organization in key for proper SSR caching per organization
  return props.organization ? `credits-search-org-${props.organization.id}` : 'credits-search'
})

const { data: creditPurposesData } = await useApi<IResponseData<ICreditPurposes[]>>(
  () => '/credit_purposes',
  { key: 'credit-purposes-list-meili-search' }
)

const { data: creditTypesData } = await useApi<IResponseData<ICreditPurposes[]>>(
  () => '/credit_types',
  { key: 'credit-types-list-meili-search' }
)

const { t } = useI18n()

const menuItems = ['KGS', 'RUB', 'USD', 'EUR', 'KZT', 'CNY']
const currencyList = useCurrencyStore().list
const currencyOptions = computed(() => [
  { id: 0, label: t('common.any_currency'), value: 'any' },
  ...currencyList
    .filter((c) => menuItems.includes(c.code))
    .map((c, i) => ({
      id: i + 1,
      label: c.code,
      value: c.code.toLowerCase()
    }))
])

const typesOptions = computed<IMultiselectOption[]>(
  () =>
    creditTypesData.value?.data.map((item) => ({
      id: item.id,
      label: item.title,
      value: item.slug
    })) ?? []
)

const creditPurposeOptions = computed<IMultiselectOption[]>(
  () =>
    creditPurposesData.value?.data.map((item) => ({
      id: item.id,
      value: item.slug,
      label: item.title
    })) ?? []
)

const initializeFiltersFromUrl = () => {
  if (route.query.q) {
    searchQueryString.value = String(route.query.q)
  }
  if (route.query.page) {
    currentPage.value = Number(route.query.page)
  } else {
    currentPage.value = 1
  }

  if (['microcredit_company', 'bank'].includes(route.query.organization as string)) {
    selectedOrganizationType.value = route.query.organization as string
  }

  if (route.params.type && typesOptions.value.length > 0) {
    const type = typesOptions.value.find((item) => item.value === route.params.type[0])
    if (type) {
      selectedType.value = [type]
    } else {
      selectedType.value = []
    }
  } else {
    selectedType.value = []
  }

  if (route.params.purpose && creditPurposeOptions.value.length > 0) {
    const purpose = creditPurposeOptions.value.find(
      (item) => item.value === route.params.purpose[0]
    )
    if (purpose) {
      selectedPurpose.value = [purpose]
    } else {
      selectedPurpose.value = []
    }
  } else {
    selectedPurpose.value = []
  }
}

initializeFiltersFromUrl()

// Active filter computation
const activeFilter = computed(() => {
  const allFilters: string[] = []

  if (props.organization) {
    allFilters.push(`organization.id = ${props.organization.id}`)
  }

  if (creditAmount.value && currency.value && currency.value !== 'any') {
    const amount = creditAmount.value
    const baseCondition = `(settings.min_amount_${currency.value} <= ${amount} AND (settings.max_amount_${currency.value} >= ${amount} OR settings.max_amount_${currency.value} = 0))`
    allFilters.push(`(${baseCondition})`)
  }

  if (selectedPeriod.value.length) {
    const periodFilters = selectedPeriod.value.map((periodOption) => {
      const periodVal = periodOption.value
      return `rates.period >= ${periodVal}`
    })

    let combined = `(${periodFilters.join(' OR ')})`

    if (currency.value && currency.value !== 'any') {
      const curId = currencyList.find((el) => el.code.toLowerCase() === currency.value)?.id
      if (curId) {
        combined = `(${combined} AND rates.currency_id = ${curId})`
      }
    }

    allFilters.push(combined)
  }

  if (selectedOrganizationType.value !== '0') {
    allFilters.push(`organization.type = "${selectedOrganizationType.value}"`)
  }

  if (selectedIncomeConfirmation.value && selectedIncomeConfirmation.value !== 'any') {
    allFilters.push(`income_confirmation = "${selectedIncomeConfirmation.value === '1'}"`)
  }

  if (selectedGuaranteeRequiredType.value && selectedGuaranteeRequiredType.value !== 'any') {
    allFilters.push(`guarantee_required = "${selectedGuaranteeRequiredType.value === '1'}"`)
  }

  if (currency.value && currency.value !== 'any') {
    allFilters.push(`settings.is_${currency.value} = true`)
  }

  if (selectedPurpose.value?.length) {
    const purposesFilter = selectedPurpose.value.map((o) => `purposes.id = ${o.id}`).join(' OR ')
    allFilters.push(`(${purposesFilter})`)
  }

  if (selectedType.value?.length) {
    const selectedTypesFilter = selectedType.value.map((o) => `types.id = ${o.id}`).join(' OR ')
    allFilters.push(`(${selectedTypesFilter})`)
  }

  return allFilters.length ? [`(${allFilters.join(' AND ')})`] : undefined
})

const { search: searchCredits } = useMeiliSearch(`${config.public.meiliEnvironment}_credits`)

// When a specific currency is selected, show and sort by that currency's values
const activeCurrency = computed(() =>
  currency.value && currency.value !== 'any' ? currency.value : undefined
)

const { settingKey, localizedSetting } = useMeiliCurrencySettings(activeCurrency)

// Use useAsyncData for SSR support with manual refresh control
const {
  data: result,
  pending: isSearching,
  error: searchError,
  refresh: refreshSearch
} = await useAsyncData<SearchResponse<ICreditCard> | null>(searchKey, async () => {
  // Sort by the selected currency's rate value, falling back to the general one
  const minRateValueSort = `settings.${settingKey('min_rate_value')}:asc`

  try {
    const searchResult = (await searchCredits(searchQueryString.value, {
      hitsPerPage,
      page: currentPage.value,
      sort: ['weight:desc', 'highlight:desc', minRateValueSort, 'updated_at:desc'],
      attributesToSearchOn: [
        'title.ky',
        'title.ru',
        'organization.title.ky',
        'organization.title.ru',
        'organization.official_title.ky',
        'organization.official_title.ru',
        'types.title.ky',
        'types.title.ru',
        'purposes.title'
      ],
      attributesToHighlight: ['title'],
      ...(activeFilter.value ? { filter: activeFilter.value } : {})
    })) as unknown as SearchResponse<ICreditCard>

    return searchResult
  } catch (e) {
    console.error('MeiliSearch fetch error:', e)
    return null
  }
})

const hasError = computed(() => !!searchError.value)

const orderedHits = shuffleHighlightedAfterMount(() => result.value?.hits || [])

const typedHits = computed(() => {
  const minRateKey = settingKey('min_rate')
  const maxPeriodKey = settingKey('max_period')

  return orderedHits.value.map((hit) => ({
    id: hit.id,
    url: hit.url,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    min_rate: localizedSetting(hit.settings, minRateKey) ?? '',
    max_period: localizedSetting(hit.settings, maxPeriodKey) ?? null,
    types: hit.types.map((type) => ({
      ...type,
      title: type.title[locale.value]
    })),
    currencies: hit.currencies,
    organization: {
      ...hit.organization,
      title: hit.organization.title?.[locale.value] || '',
      official_title: hit.organization.official_title?.[locale.value] || ''
    },
    highlight: hit.highlight,
    is_islamic: hit.is_islamic,
    ratings: hit.ratings ? hit.ratings.average_rate : null
  }))
})

const isOpen = ref(false)

// Watch for search query changes - automatic with debounce
watch(debouncedQuery, () => {
  if (currentPage.value !== 1) currentPage.value = 1

  // Trigger search automatically for query changes
  refreshSearch()

  // Update URL
  if (!isOpen.value) {
    router.replace({
      path: route.path,
      query: {
        ...route.query,
        q: debouncedQuery.value || undefined,
        page: currentPage.value > 1 ? String(currentPage.value) : undefined
      }
    })
  }
})

// Watch for manual filter changes - only when user interacts with form
watch(
  [
    currency,
    selectedPeriod,
    selectedOrganizationType,
    selectedIncomeConfirmation,
    selectedGuaranteeRequiredType,
    creditAmount
  ],
  () => {
    // Skip during initialization or when dialog is open
    if (isOpen.value) return

    if (currentPage.value !== 1) currentPage.value = 1

    // Trigger search for manual filter changes
    refreshSearch()

    // Update URL
    router.replace({
      path: route.path,
      query: {
        ...route.query,
        q: debouncedQuery.value || undefined,
        page: currentPage.value > 1 ? String(currentPage.value) : undefined
      }
    })
  }
)

const toggle = () => {
  isOpen.value = !isOpen.value
}

onBeforeRouteLeave(() => {
  isOpen.value = false
})

const changePage = (page: number) => {
  currentPage.value = page
  refreshSearch()
}

onMounted(async () => {
  if (useLocalFilters.value) {
    localCurrentPage.value = 1
    localSelectedPurpose.value = []
    localSelectedType.value = []
  } else {
    filtersStore.page = 1
    filtersStore.selectedPurpose = []
    filtersStore.selectedType = []
  }

  await nextTick()

  initializeFiltersFromUrl()
})

const resetFilters = () => {
  if (useLocalFilters.value) {
    localSearchQueryString.value = ''
    localCreditAmountRaw.value = undefined
    localCurrency.value = 'any'
    localSelectedPeriod.value = []
    localSelectedOrganizationType.value = String(organizationType[0].value)
    localSelectedIncomeConfirmation.value = undefined
    localSelectedGuaranteeRequiredType.value = undefined
    localSelectedPurpose.value = []
    localSelectedType.value = []
    localCurrentPage.value = 1
  } else {
    filtersStore.reset()
  }
  // Clear state on reset
  searchQueryState.value = ''
}

const clearSearch = () => {
  if (useLocalFilters.value) {
    localSearchQueryString.value = ''
  } else {
    filtersStore.searchQuery = ''
  }
  searchQueryState.value = ''
}

const applyFilters = () => {
  isOpen.value = false

  // Trigger search when filters are applied from dialog
  refreshSearch()

  router.replace({
    path: route.path,
    query: {
      ...route.query,
      q: debouncedQuery.value || undefined,
      page: currentPage.value > 1 ? String(currentPage.value) : undefined
    }
  })
}
</script>

<template>
  <div class="space-y-5">
    <div class="relative">
      <Input
        v-model="searchQueryString"
        :placeholder="$t('common.search_organization_placeholder')"
        class="h-[52px] pl-14 pr-14"
      />
      <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
      <Button
        v-if="searchQueryString"
        size="icon"
        variant="link"
        class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
        @click="clearSearch"
      >
        <LucideCircleX />
      </Button>
    </div>

    <div class="flex items-start justify-between gap-2">
      <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
        <AmountCurrencySelector
          :amount="creditAmountRaw"
          :currency="currency"
          :currency-options="currencyOptions"
          height="h-[52px]"
          @update:amount="creditAmountRaw = $event"
          @update:currency="currency = $event"
        />

        <MultiSelect
          v-model="selectedPeriod"
          class="multiselect_height_52"
          :options="periodOptions"
          :placeholder="$t('common.period_placeholder')"
        />
        <MultiSelect
          v-model="selectedPurpose"
          :options="creditPurposeOptions"
          :placeholder="$t('common.credit_purpose')"
          class="multiselect_height_52"
          @update:model-value="refreshSearch()"
        />
      </div>
      <Button
        variant="blue"
        size="icon"
        class="mt-0.5 !p-6"
        :aria-label="$t('common.all_filters')"
        @click="toggle"
      >
        <SlidersVertical class="h-6 w-6" />
      </Button>
    </div>

    <section v-if="!isSearching && !hasError && result" aria-labelledby="credits">
      <div v-if="result.hits?.length" class="CardsList">
        <Heading id="credits" tag="h2" class="sr-only">{{ $t('common.credits') }}</Heading>
        <EntityCreditItem
          v-for="item in typedHits"
          :key="item.id"
          :item="item"
          :hide-organization-details="!!organization"
          :selected-currency="activeCurrency"
        />

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev:
                currentPage > 1
                  ? `?q=${searchQueryString}&page=${result.page ? result.page - 1 : 1}`
                  : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? `?q=${searchQueryString}&page=${result.page ? result.page + 1 : 1}`
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityCreditItemSkeleton :count="hitsPerPage" :hide-organization-details="!!organization" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>

    <Dialog v-model:open="isOpen">
      <DialogContent overlay-class="bg-gray-600/50" class="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{{ $t('common.all_filters') }}</DialogTitle>
        </DialogHeader>
        <div class="grid grid-cols-1 gap-2 sm:gap-5">
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <AmountCurrencySelector
              :amount="creditAmountRaw"
              :currency="currency"
              :currency-options="currencyOptions"
              height="h-[52px]"
              @update:amount="creditAmountRaw = $event"
              @update:currency="currency = $event"
            />

            <MultiSelect
              v-model="selectedPeriod"
              class="multiselect_height_52"
              :options="periodOptions"
              :placeholder="$t('common.period_placeholder')"
            />
          </div>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <MultiSelect
              v-model="selectedPurpose"
              :options="creditPurposeOptions"
              :placeholder="$t('common.credit_purpose')"
              class="multiselect_height_52"
            />

            <Select v-model="selectedOrganizationType">
              <SelectTrigger class="w-full" variant="searchForm">
                <SelectValue :placeholder="$t('common.organization_label')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="option in organizationType"
                  :key="option.id"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Select v-model="selectedIncomeConfirmation">
              <SelectTrigger class="w-full" variant="searchForm">
                <SelectValue
                  :placeholder="
                    $t('widget.credits.list_meili_search.income_confirmation_placeholder')
                  "
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="option in incomeConfirmationTypes"
                  :key="option.id"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
            <Select v-model="selectedGuaranteeRequiredType">
              <SelectTrigger class="w-full" variant="searchForm">
                <SelectValue
                  :placeholder="$t('widget.credits.list_meili_search.collateral_placeholder')"
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="option in guaranteeRequiredTypes"
                  :key="option.id"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter class="gap-2">
          <Button variant="outline-blue" @click="resetFilters">{{
            $t('common.reset_filters')
          }}</Button>
          <Button variant="blue" @click="applyFilters">{{ $t('common.show') }}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
